import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HamburgerIcon from '../../assets/icons/HamburgerIcon';
import styles from './Navbar.module.css';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
    const { isAuthenticated, logout, currentUser } = useAuth();

    const navbarRef = useRef(null);
    const moreDropdownRef = useRef(null);
    const isAdmin = currentUser?.role === 'Administrator';

    const updateNavbarHeightProperty = useCallback(() => {
        if (navbarRef.current) {
            const newHeight = navbarRef.current.offsetHeight;
            document.documentElement.style.setProperty('--navbar-height', `${newHeight}px`);
        }
    }, []);

    useEffect(() => {
        updateNavbarHeightProperty();
        const resizeObserver = new ResizeObserver(updateNavbarHeightProperty);
        const navElement = navbarRef.current;

        if (navElement) {
            resizeObserver.observe(navElement);
        }

        window.addEventListener('resize', updateNavbarHeightProperty);

        return () => {
            if (navElement) {
                resizeObserver.unobserve(navElement);
            }
            window.removeEventListener('resize', updateNavbarHeightProperty);
        };
    }, [updateNavbarHeightProperty]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
                setIsMoreDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    const closeMenu = () => {
        setIsMenuOpen(false);
        setIsMoreDropdownOpen(false);
    };

    const toggleMoreDropdown = (e) => {
        e.preventDefault();
        setIsMoreDropdownOpen(!isMoreDropdownOpen);
    };

    const handleLogout = () => {
        closeMenu();
        logout();
    };

    const getNavLinkClass = ({ isActive }) =>
        isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink;

    // Links shown to non-authenticated users desktop, and everyone on mobile
    const publicLinksFlat = (
        <>
            <li><NavLink to="/about" className={getNavLinkClass} onClick={closeMenu}>About</NavLink></li>
            <li><NavLink to="/gallery" className={getNavLinkClass} onClick={closeMenu}>Gallery</NavLink></li>
            <li><NavLink to="/facilities" className={getNavLinkClass} onClick={closeMenu}>Facilities</NavLink></li>
            <li><NavLink to="/request-reservation" className={getNavLinkClass} onClick={closeMenu}>Reservations</NavLink></li>
            <li><NavLink to="/contact" className={getNavLinkClass} onClick={closeMenu}>Contact</NavLink></li>
        </>
    );

    // Links shown in the "More" dropdown for authenticated users
    const publicLinksDropdown = (
        <li className={styles.dropdownContainer} ref={moreDropdownRef}>
            <button
                className={`${styles.navLink} ${styles.dropdownToggle}`}
                onClick={toggleMoreDropdown}
                aria-expanded={isMoreDropdownOpen}
            >
                More
                <span className={`${styles.dropdownArrow} ${isMoreDropdownOpen ? styles.dropdownArrowOpen : ''}`}>▼</span>
            </button>
            <ul className={`${styles.dropdownMenu} ${isMoreDropdownOpen ? styles.dropdownMenuOpen : ''}`}>
                <li><NavLink to="/about" className={styles.dropdownLink} onClick={closeMenu}>About</NavLink></li>
                <li><NavLink to="/gallery" className={styles.dropdownLink} onClick={closeMenu}>Gallery</NavLink></li>
                <li><NavLink to="/facilities" className={styles.dropdownLink} onClick={closeMenu}>Facilities</NavLink></li>
                <li><NavLink to="/request-reservation" className={styles.dropdownLink} onClick={closeMenu}>Reservations</NavLink></li>
                <li><NavLink to="/contact" className={styles.dropdownLink} onClick={closeMenu}>Contact</NavLink></li>
            </ul>
        </li>
    );

    // Extra links only for logged-in users
    const authLinks = isAuthenticated && (
        <>
            <li><NavLink to="/account" className={getNavLinkClass} onClick={closeMenu}>My Account</NavLink></li>
            {isAdmin && (
                <li><NavLink to="/admin" className={getNavLinkClass} onClick={closeMenu}>Admin</NavLink></li>
            )}
        </>
    );

    return (
        <nav className={`${styles.navbar} ${isMenuOpen ? styles.navbarMobileOpen : ''}`} ref={navbarRef}>
            <div className={styles.navbarContentContainer}>
                <div className={styles.logo}>
                    <NavLink to="/home" onClick={closeMenu} className={styles.logoLink} style={{ textDecoration: 'none', color: '#ffffff' }}>Highview House</NavLink>
                </div>

                {/* Desktop Navigation - flat single row */}
                <ul className={styles.navLinksDesktop}>
                    {isAuthenticated ? publicLinksDropdown : publicLinksFlat}
                    {authLinks}
                </ul>

                {/* Mobile Navigation - flat list */}
                <ul className={`${styles.navLinksMobile} ${isMenuOpen ? styles.navLinksMobileOpen : ''}`}>
                    {publicLinksFlat}
                    {authLinks}
                    <li className={styles.mobileLoginButton}>
                        {isAuthenticated ? (
                            <button onClick={handleLogout} className={styles.ctaButton}>Logout</button>
                        ) : (
                            <NavLink to="/login" className={styles.ctaButton} onClick={closeMenu}>Login</NavLink>
                        )}
                    </li>
                </ul>

                <div className={styles.navActions}>
                    {isAuthenticated ? (
                        <button onClick={handleLogout} className={styles.ctaButton}>Logout</button>
                    ) : (
                        <NavLink to="/login" className={styles.ctaButton}>Login</NavLink>
                    )}
                </div>

                <button className={styles.hamburgerButton} onClick={toggleMenu} aria-label="Toggle menu">
                    <HamburgerIcon color="var(--text-light)" />
                </button>
            </div>
        </nav>
    );
};

export default Navbar;
